import React, { useState, useEffect, useCallback } from "react";
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert, Snackbar, TextField, Select, MenuItem, TablePagination, InputAdornment, Tabs, Tab, useTheme, alpha } from "@mui/material";
import { ContentCopy as CopyIcon, AttachMoney as MoneyIcon, LocalOffer as DiscountIcon, Search as SearchIcon, Link as LinkIcon, Gavel as RulesIcon, People as PeopleIcon, Payments as PaymentsIcon } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import api from "../../services/api";
import { IClient, IUserDashboard, IPayment, ClientStatus, PaymentStatus, CLIENT_STATUS_LABELS, CLIENT_STATUS_COLORS, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from "../../types";
import { useBranding } from "../../context/BrandingContext";

interface IPlatformSettings {
  default_commission_value: number;
  client_discount: number;
  client_discount_type: "fixed" | "percentage";
  commission_rules_text: string;
}

const AffiliateDashboard: React.FC = () => {
  const theme = useTheme();
  const { branding } = useBranding();
  const [tabValue, setTabValue] = useState(0);
  const [dashboard, setDashboard] = useState<IUserDashboard | null>(null);
  const [platformSettings, setPlatformSettings] = useState<IPlatformSettings | null>(null);
  const [clients, setClients] = useState<IClient[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [totalPayments, setTotalPayments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Filtros e paginação para indicados
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ClientStatus | "">("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filtros e paginação para pagamentos
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<PaymentStatus | "">("");
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get("/users/dashboard");
      setDashboard(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar dados");
    }
  }, []);

  const fetchPlatformSettings = useCallback(async () => {
    try {
      const response = await api.get("/settings/platform/public");
      setPlatformSettings(response.data.data);
    } catch {
      setPlatformSettings({ default_commission_value: 0, client_discount: 0, client_discount_type: "fixed", commission_rules_text: "" });
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const params: any = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get("/clients/my-clients", { params });
      setClients(response.data.data.clients);
      setTotalClients(response.data.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar indicados");
    }
  }, [page, rowsPerPage, search, statusFilter]);

  const fetchPayments = useCallback(async () => {
    try {
      const params: any = {
        skip: paymentPage * paymentRowsPerPage,
        limit: paymentRowsPerPage,
      };
      if (paymentStatusFilter) params.status = paymentStatusFilter;

      const response = await api.get("/payments/my-payments", { params });
      setPayments(response.data.data.payments);
      setTotalPayments(response.data.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao carregar pagamentos");
    }
  }, [paymentPage, paymentRowsPerPage, paymentStatusFilter]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchPlatformSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchPlatformSettings]);

  useEffect(() => {
    if (tabValue === 2) {
      fetchClients();
    }
  }, [fetchClients, tabValue]);

  useEffect(() => {
    if (tabValue === 3) {
      fetchPayments();
    }
  }, [fetchPayments, tabValue]);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleStatusFilterChange = (value: ClientStatus | "") => {
    setStatusFilter(value);
    setPage(0);
  };

  const copyLink = () => {
    if (dashboard?.affiliate_link) {
      navigator.clipboard.writeText(dashboard.affiliate_link);
      setCopied(true);
    }
  };

  const formatDiscount = () => {
    if (!dashboard?.client_discount) return "nenhum desconto especial";
    const { value, type } = dashboard.client_discount;
    if (value === 0) return "nenhum desconto especial";
    return type === "fixed" ? `R$ ${value.toFixed(2)} de desconto` : `${value}% de desconto`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboard) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Meu Painel de Indicação
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            minWidth: { xs: 48, sm: 160 },
            px: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Tab
          icon={<LinkIcon />}
          iconPosition="start"
          label={
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Link
            </Box>
          }
        />
        <Tab
          icon={<RulesIcon />}
          iconPosition="start"
          label={
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Regras
            </Box>
          }
        />
        <Tab
          icon={<PeopleIcon />}
          iconPosition="start"
          label={
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Indicados
            </Box>
          }
        />
        <Tab
          icon={<PaymentsIcon />}
          iconPosition="start"
          label={
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Pagamentos
            </Box>
          }
        />
      </Tabs>

      {/* Aba 1: Link de Indicação */}
      {tabValue === 0 && (
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", gap: 4, flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "center" }}>
                <Box sx={{ flex: 1, minWidth: 280 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LinkIcon color="primary" />
                    Seu Link de Indicação
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                      p: 2,
                      borderRadius: 1,
                      mt: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontFamily: "monospace",
                        fontSize: "0.95rem",
                      }}
                    >
                      {dashboard?.affiliate_link}
                    </Typography>
                    <IconButton onClick={copyLink} color="primary" size="large">
                      <CopyIcon />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Compartilhe este link com seus contatos para que eles se cadastrem e você ganhe sua comissão!
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "center", p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    QR Code
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "white",
                      borderRadius: 2,
                      display: "inline-block",
                      boxShadow: 1,
                    }}
                  >
                    {dashboard?.affiliate_link && <QRCodeSVG value={dashboard.affiliate_link} size={160} level="H" includeMargin={false} fgColor={branding.colors.sidebarStart} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Escaneie para acessar
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Mesmo gradiente da barra superior (MuiAppBar no tema) */}
          <Card
            sx={{
              background: `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`,
              color: "#fff",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DiscountIcon sx={{ color: branding.colors.primary }} />
                Benefícios para quem você indicar
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                Ao acessar seu link de indicação, seu contato terá acesso a{" "}
                <Box component="strong" sx={{ color: branding.colors.primary }}>
                  {formatDiscount()}
                </Box>{" "}
                em nossos serviços.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "rgba(255,255,255,0.85)" }}>
                Compartilhe seu link com amigos, familiares e conhecidos para que eles aproveitem essa vantagem exclusiva!
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Aba 2: Regras de Comissão */}
      {tabValue === 1 && (
        <Box>
          <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.success.main, 0.1) }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MoneyIcon sx={{ color: "success.main", mr: 1, fontSize: 32 }} />
                <Typography variant="h6">Sua Comissão por Fechamento</Typography>
              </Box>
              <Typography variant="h2" fontWeight="bold" color="success.main">
                R$ {dashboard?.commission.value.toFixed(2)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Este é o valor que você recebe cada vez que um cliente indicado por você fecha negócio conosco.
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RulesIcon color="primary" />
                Regras e Condições
              </Typography>
              {platformSettings?.commission_rules_text ? (
                <Box
                  sx={{
                    mt: 2,
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                      mt: 2,
                      mb: 1,
                      fontWeight: 600,
                      color: "text.primary",
                    },
                    "& h3": {
                      fontSize: "1.1rem",
                    },
                    "& p": {
                      mb: 2,
                      lineHeight: 1.7,
                      color: "text.secondary",
                    },
                    "& ul, & ol": {
                      pl: 3,
                      mb: 2,
                    },
                    "& li": {
                      mb: 1,
                      lineHeight: 1.6,
                      color: "text.secondary",
                    },
                    "& strong": {
                      fontWeight: 600,
                      color: "text.primary",
                    },
                    "& a": {
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: platformSettings.commission_rules_text }}
                />
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  As regras de comissão ainda não foram configuradas. Entre em contato com o suporte para mais informações.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Aba 3: Clientes Indicados */}
      {tabValue === 2 && (
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Meus Indicados
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Buscar por nome, email ou telefone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Select size="small" value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value as ClientStatus | "")} displayEmpty sx={{ minWidth: 200 }}>
              <MenuItem value="">Todos os status</MenuItem>
              {Object.entries(CLIENT_STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {search || statusFilter ? "Nenhum indicado encontrado com os filtros aplicados" : "Você ainda não indicou ninguém. Compartilhe seu link!"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>{client.email}</TableCell>
                      <TableCell>{client.phone}</TableCell>
                      <TableCell>
                        <Chip label={CLIENT_STATUS_LABELS[client.status]} color={CLIENT_STATUS_COLORS[client.status]} size="small" />
                      </TableCell>
                      <TableCell>{new Date(client.created_at).toLocaleDateString("pt-BR")}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalClients}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
            />
          </TableContainer>
        </Box>
      )}

      {/* Aba 4: Pagamentos */}
      {tabValue === 3 && (
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Meus Pagamentos
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Select
              size="small"
              value={paymentStatusFilter}
              onChange={(e) => {
                setPaymentStatusFilter(e.target.value as PaymentStatus | "");
                setPaymentPage(0);
              }}
              displayEmpty
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos os status</MenuItem>
              <MenuItem value="pending">Pendentes</MenuItem>
              <MenuItem value="paid">Pagos</MenuItem>
              <MenuItem value="cancelled">Cancelados</MenuItem>
            </Select>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell>Pago em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        {paymentStatusFilter ? "Nenhum pagamento encontrado com o filtro aplicado" : "Você ainda não possui pagamentos. Quando um indicado fechar negócio, seu pagamento aparecerá aqui!"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.client?.name || "-"}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color="success.main">
                          R$ {payment.commission_value.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={PAYMENT_STATUS_LABELS[payment.status]} color={PAYMENT_STATUS_COLORS[payment.status]} size="small" />
                      </TableCell>
                      <TableCell>{new Date(payment.created_at).toLocaleDateString("pt-BR")}</TableCell>
                      <TableCell>{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString("pt-BR") : "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalPayments}
              page={paymentPage}
              onPageChange={(_, newPage) => setPaymentPage(newPage)}
              rowsPerPage={paymentRowsPerPage}
              onRowsPerPageChange={(e) => {
                setPaymentRowsPerPage(parseInt(e.target.value, 10));
                setPaymentPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
            />
          </TableContainer>
        </Box>
      )}

      <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)} message="Link copiado!" />
    </Box>
  );
};

export default AffiliateDashboard;
